import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ChargeCodesPicker from "@/components/ChargeCodesPicker";
import { formatDate } from "@/lib/dates";
import { toast } from "sonner";

// Labour hours between two HH:MM times (0 if invalid/negative).
function hoursBetween(start, finish) {
  try {
    const [sh, sm] = (start || "").split(":").map(Number);
    const [fh, fm] = (finish || "").split(":").map(Number);
    const mins = (fh * 60 + fm) - (sh * 60 + sm);
    return mins > 0 ? Math.round((mins / 60) * 100) / 100 : 0;
  } catch { return 0; }
}

// Per-DAY edit: charge codes, description of works, worker & times for a SINGLE docket/day.
export default function EditDayDialog({ open, setOpen, jobId, index, onSaved }) {
  const [f, setF] = useState({});
  const [meta, setMeta] = useState({ docket_number: "", visit_date: "" });
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !jobId || index == null || index < 0) return;
    setLoading(true);
    Promise.all([api.get(`/jobs/${jobId}`), api.get("/assignable-workers")])
      .then(([jr, wr]) => {
        setTechs(wr.data || []);
        const d = (jr.data.dockets || [])[index] || {};
        const tos = d.time_on_site || {};
        setMeta({ docket_number: d.docket_number || `Day ${index + 1}`, visit_date: d.visit_date || jr.data.appointment_date });
        const arrival = tos.arrival_time || d.start_time || jr.data.start_time || "";
        const finish = tos.finish_time || d.finish_time || jr.data.finish_time || "";
        const hrs = (tos.hours_on_site != null && tos.hours_on_site !== "") ? tos.hours_on_site : hoursBetween(arrival, finish);
        setF({
          charge_codes: (d.charge_codes || []).map((c) => ({ ...c, rate: c.rate ?? "" })),
          description_of_works: d.description_of_works || "",
          start_time: arrival, finish_time: finish,
          hours_on_site: hrs,
          worker_id: d.worker_id || d.completed_by_id || "",
        });
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, jobId, index]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  // Editing arrival/finish auto-updates on-site hours (admin can still override the number below).
  const setTime = (k, v) => setF((s) => {
    const arrival = k === "start_time" ? v : s.start_time;
    const finish = k === "finish_time" ? v : s.finish_time;
    return { ...s, [k]: v, hours_on_site: hoursBetween(arrival, finish) };
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, hours_on_site: (f.hours_on_site === "" || f.hours_on_site == null) ? null : Number(f.hours_on_site) };
      await api.patch(`/jobs/${jobId}/dockets/${index}/edit`, payload);
      toast.success("Day updated (audit recorded). Any draft invoice was refreshed.");
      setOpen(false); onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-day-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Day — {meta.docket_number}{meta.visit_date ? ` · ${formatDate(meta.visit_date)}` : ""}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs text-slate-400 -mt-2">Edits apply to this day only. Every change is recorded in the audit history. Draft invoices auto-refresh.</DialogDescription>
        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">Loading day…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Worker (this day)</Label>
                <Select value={f.worker_id || undefined} onValueChange={(v) => set("worker_id", v)}>
                  <SelectTrigger data-testid="ed-worker" className="mt-1"><SelectValue placeholder="Select worker…" /></SelectTrigger>
                  <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.role === "contractor" ? " (Contractor)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div />
              <div><Label>Arrival Time</Label><Input data-testid="ed-arrival" type="time" value={f.start_time} onChange={(e) => setTime("start_time", e.target.value)} className="mt-1" /></div>
              <div><Label>Finish Time</Label><Input data-testid="ed-finish" type="time" value={f.finish_time} onChange={(e) => setTime("finish_time", e.target.value)} className="mt-1" /></div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label>Hours On Site <span className="text-slate-400 font-normal">(drives labour cost & profitability)</span></Label>
                  <Input data-testid="ed-hours" type="number" step="0.25" min="0" value={f.hours_on_site ?? ""} onChange={(e) => set("hours_on_site", e.target.value === "" ? "" : Number(e.target.value))} className="mt-1 w-32" />
                </div>
                <button type="button" data-testid="ed-hours-sync" onClick={() => set("hours_on_site", hoursBetween(f.start_time, f.finish_time))}
                  className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline pb-2">
                  Use arrival → finish ({hoursBetween(f.start_time, f.finish_time)}h)
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Auto-fills from arrival/finish. Adjust it directly to reflect actual paid time on site (e.g. minus a lunch break).</p>
            </div>
            <div><Label>Description of Works</Label><Textarea data-testid="ed-description" value={f.description_of_works} onChange={(e) => set("description_of_works", e.target.value)} rows={5} className="mt-1" /></div>
            <div>
              <Label>Charge Codes, Quantities & Rates <span className="text-slate-400 font-normal">(this day · Admin rate override)</span></Label>
              <div className="mt-1"><ChargeCodesPicker value={f.charge_codes} onChange={(v) => set("charge_codes", v)} showRate jobId={jobId} /></div>
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="ed-save" onClick={save} disabled={saving || loading} className="bg-navy hover:bg-navy-light">{saving ? "Saving…" : "Save Changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
